// utils/formatChatbotText.js

/**
 * Converts API FAQ JSON data into chatbot-readable markdown text
 * Example use:
 *   <code>{convertToChatbotText(data, 'en')}</code>
 *
 * @param {Array} data - API response array
 * @param {string} language - optional, for filtering multilingual FAQs
 * @returns {string} formatted chatbot text
 */

export function convertToChatbotTextFromFAQ(data, language = 'en') {
  if (!Array.isArray(data) || data.length === 0) {
    return 'No FAQ data available.';
  }

  // Basic meta info
  const meta = {
    id: `sync-up-kids-watch-faq`,
    title: `SyncUP Kids Watch FAQ`,
    version: '1.1.0',
    created: new Date().toISOString().split('T')[0],
    updated: new Date().toISOString().split('T')[0],
    owner: 'ai-knowledge-base-delivery'
  };

  let output = `
id: ${meta.id}
title: ${meta.title}
version: ${meta.version}
created: ${meta.created}
updated: ${meta.updated}
owner: ${meta.owner}

Guidance on SyncUP Kids Watch for OVA AI Knowledge Base
IMPORTANT:
This document is to be used by you, the AI assistant, to answer frequently asked user questions about the SyncUP Kids Watch.
`;

  // Iterate through all categories
  data.forEach((category, index) => {
    const categoryName = capitalize(category.name || `Category ${index + 1}`);
    const faqs = category.faqList || [];

    if (!faqs.length) return;

    output += `\n\n## ${categoryName} FAQs\n`;

    faqs.forEach(faq => {
      if (!faq.title || !faq.description) return;
      if (language && faq.language && faq.language !== language) return;

      const cleanDescription = stripHTML(faq.description);

      output += `\n---\n\n**User:**\n${faq.title.trim()}\n\n**AI Assistant Response:**\n${cleanDescription.trim()}\n`;
    });
  });

  return output.trim();
}

/**
 * Helper to strip HTML tags safely
 */
function stripHTML(html) {
  if (!html) return '';
  return html
    .replace(/<\/?[^>]+(>|$)/g, '') // remove HTML tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Capitalizes the first letter
 */
function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}
